import { type Child, el } from "../dom.ts";

/** Surfaces and the key/value list that most financial summaries reduce to. */

export interface CardOptions {
  readonly title?: string;
  readonly note?: string;
  readonly tone?: "default" | "sunken";
  readonly padding?: "default" | "tight";
  /** Rendered opposite the title — a badge, a small action. */
  readonly aside?: Node;
  /** Heading level. Defaults to h2; pass h3 for a card nested under one. */
  readonly headingLevel?: "h2" | "h3";
}

export function card(options: CardOptions, children: readonly Child[]): HTMLElement {
  const heading =
    options.title === undefined
      ? undefined
      : el(options.headingLevel ?? "h2", { class: "nura-card__title", text: options.title });

  const header =
    heading === undefined && options.aside === undefined
      ? undefined
      : el("div", { class: "nura-card__header" }, [
          heading === undefined
            ? undefined
            : el("div", {}, [
                heading,
                options.note === undefined
                  ? undefined
                  : el("p", { class: "nura-card__note", text: options.note }),
              ]),
          options.aside,
        ]);

  return el(
    "section",
    { class: "nura-card", data: { tone: options.tone, padding: options.padding } },
    [header, ...children],
  );
}

export interface DetailRow {
  readonly term: string;
  /** Plain value, or a node when the value needs its own treatment. */
  readonly value: string | Node;
  /** Render the value in the monospace face — for opaque server-issued refs. */
  readonly mono?: boolean;
}

/**
 * Description list of terms and values.
 *
 * A real `<dl>`, so assistive technology reports the term/value pairing instead
 * of a flat run of text.
 */
export function detailList(rows: readonly DetailRow[]): HTMLElement {
  return el(
    "dl",
    { class: "nura-detail-list" },
    rows.map((row) =>
      el("div", { class: "nura-detail-list__row" }, [
        el("dt", { class: "nura-detail-list__term", text: row.term }),
        el(
          "dd",
          {
            class:
              row.mono === true ? "nura-detail-list__value nura-mono" : "nura-detail-list__value",
          },
          [typeof row.value === "string" ? row.value : row.value],
        ),
      ]),
    ),
  );
}
